/**
 * v1.74.0 — Wizard Casa refactor completo.
 *
 * Sostituisce il vecchio CasaSteps.jsx:
 *   - Config-driven (fetch /api/public/casa/prodotto)
 *   - Step0 Dati immobile: nuovi campi CGA (dimora, tipologia costruttiva,
 *     qualità finiture, anno costruzione, anno ristrutturazione, ISTAT)
 *   - Step Valutazione: chiama /api/public/casa/stima-ricostruzione (nuovo motore)
 *   - Step Copertura: accordion 3 partite (Incendio obbligatoria + Furto/RC
 *     toggle) con facoltative annidate come cards + tooltip glossario
 *
 * NIENTE menzioni "Reale Mutua" / "CasaMia" — spersonalizzato per il cliente.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import GeoLocationInput from "@/components/GeoLocationInput";
import InfoTooltip from "@/pages/public/components/InfoTooltip";
import { Loader2, RefreshCw, ChevronDown, ChevronRight, Home, Shield, Users, Lock, Scale, HeartPulse, LifeBuoy, Square, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENT_YEAR = new Date().getFullYear();

// ─────────────────────────────────────────────────────────────
// Config loader (cache in modulo, non ricarica su re-render)
// ─────────────────────────────────────────────────────────────
let _CONFIG_CACHE = null;
let _ISTAT_CACHE = null;

async function loadConfig() {
  if (_CONFIG_CACHE) return _CONFIG_CACHE;
  const r = await axios.get(`${API}/public/casa/prodotto`, {
    withCredentials: true,
    timeout: 8000,
  });
  _CONFIG_CACHE = r.data;
  return _CONFIG_CACHE;
}

async function loadIstat() {
  if (_ISTAT_CACHE) return _ISTAT_CACHE;
  try {
    const r = await axios.get(`${API}/public/casa/istat-inflation`, {
      withCredentials: true,
      timeout: 5000,
    });
    _ISTAT_CACHE = r.data;
  } catch {
    _ISTAT_CACHE = { indice_foi: { anno_riferimento: null, variazione_annua_pct: null } };
  }
  return _ISTAT_CACHE;
}

function useConfig() {
  const [config, setConfig] = useState(_CONFIG_CACHE);
  const [istat, setIstat] = useState(_ISTAT_CACHE);
  const [error, setError] = useState("");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancel = false;
    if (!_CONFIG_CACHE) {
      setError("");
      loadConfig()
        .then((d) => { if (!cancel) setConfig(d); })
        .catch((e) => {
          if (cancel) return;
          const detail = e?.response?.data?.detail;
          const msg = typeof detail === "string" ? detail : (e?.message || "Errore rete");
          setError(msg);
        });
    }
    if (!_ISTAT_CACHE) loadIstat().then((d) => { if (!cancel) setIstat(d); }).catch(() => {});
    return () => { cancel = true; };
  }, [nonce]);

  const retry = () => {
    _CONFIG_CACHE = null;
    _ISTAT_CACHE = null;
    setConfig(null); setIstat(null); setError("");
    setNonce((n) => n + 1);
  };
  return { config, istat, error, retry };
}

function eurFmt(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("it-IT",
    { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

// ═════════════════════════════════════════════════════════════
// STEP 0 — Dati immobile
// ═════════════════════════════════════════════════════════════
export function CasaStep0({ formData, update }) {
  const { config, istat, error, retry } = useConfig();

  // Default ISTAT ON al primo mount — DEVE stare prima di qualsiasi early return
  // (React hooks: called unconditionally).
  useEffect(() => {
    if (formData.adeguamento_istat === undefined) update("adeguamento_istat", true);
  }, []); // eslint-disable-line

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800 space-y-3"
           data-testid="casa-config-error">
        <p><strong>Errore caricamento configurazione.</strong> {error}</p>
        <button type="button" onClick={retry}
          className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
          data-testid="casa-config-retry-btn">
          Riprova
        </button>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-400"
           data-testid="casa-config-loading">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Caricamento configurazione…
      </div>
    );
  }

  const gloss = config.glossario || {};
  const idi = config.input_dati_immobile || {};
  const archetipoCfg = idi.archetipo || {
    options: ["abitazione_singola", "residenziale_multipiano"],
    labels_cliente: {
      abitazione_singola: "Casa indipendente / villa",
      residenziale_multipiano: "Appartamento in condominio",
    },
    descrizioni_cliente: {
      abitazione_singola: "Villa, casa singola o bifamiliare — edificio autonomo, senza altre unità abitative sopra o sotto la tua.",
      residenziale_multipiano: "Appartamento in palazzo o condominio con più unità abitative, anche al piano terra o all'ultimo piano (attico, loft, mansarda).",
    },
  };
  const sistemaCos = idi.sistema_costruttivo || idi.caratteristiche_costruttive || { options: [], labels_cliente: {} };
  const prestFin = idi.prestazione || idi.qualita_finiture || { options: [], labels_cliente: {} };
  const dimora = idi.dimora || { options: ["abituale", "saltuaria"], labels_cliente: {} };

  // v1.76.0 — nuovo motore: mostra pct cumulata dinamicamente
  const istatPct = istat?.indice_foi?.variazione_annua_pct
                   ?? istat?.indice_foi?.anni?.[String(istat?.indice_foi?.ultimo_anno_disponibile)];
  const istatAnno = istat?.indice_foi?.anno_riferimento
                    ?? istat?.indice_foi?.ultimo_anno_disponibile;

  return (
    <div className="space-y-5" data-testid="casa-step0">
      {/* v1.76.0 — Archetipo cliente-facing: 2 card esplicite (Christian: "spiega cosa significano") */}
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14] flex items-center">
          Tipo di abitazione
          <InfoTooltip title="Tipo di abitazione"
            body="Casa indipendente / villa: edificio autonomo, senza altre unità abitative sopra o sotto la tua. Appartamento in condominio: unità in palazzo con più appartamenti (anche attico, loft, mansarda)."
            size={14} testid="tip-tipo-abitazione" />
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="archetipo-block">
          {archetipoCfg.options.map((k) => {
            const on = formData.archetipo === k;
            return (
              <button
                key={k}
                type="button"
                data-testid={`radio-archetipo-${k}`}
                onClick={() => update("archetipo", k)}
                className={`text-left rounded-xl border-2 p-4 transition-all ${
                  on
                    ? "border-[var(--brand-accent,#163321)] bg-[#163321]/5 ring-2 ring-[var(--brand-accent,#163321)]/20"
                    : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
                }`}
              >
                <div className="text-base font-semibold text-[#0F1F14]">
                  {archetipoCfg.labels_cliente?.[k] || k}
                </div>
                <div className="text-sm text-[#4A5E51] mt-1">
                  {archetipoCfg.descrizioni_cliente?.[k] || ""}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Dimora abituale/saltuaria */}
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14] flex items-center">
          Dimora
          <InfoTooltip title="Dimora abituale vs saltuaria" body={gloss.dimora_abituale?.body || ""} size={14} testid="tip-dimora" />
        </Label>
        <RadioGroup value={formData.dimora || ""} onValueChange={(v) => update("dimora", v)} className="flex flex-wrap gap-3">
          {dimora.options.map((o) => (
            <label key={o}
              className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer flex-1 min-w-[180px] ${formData.dimora === o ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB]"}`}
              data-testid={`dimora-${o}`}>
              <RadioGroupItem value={o} id={`dim-${o}`} />
              <span className="text-sm">{dimora.labels_cliente[o] || o}</span>
            </label>
          ))}
        </RadioGroup>
      </div>

      {/* Sistema costruttivo + Prestazione finiture */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Sistema costruttivo</Label>
          <Select value={formData.sistema_costruttivo || ""} onValueChange={(v) => update("sistema_costruttivo", v)}>
            <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="sistema-costruttivo-select">
              <SelectValue placeholder="Seleziona" />
            </SelectTrigger>
            <SelectContent>
              {sistemaCos.options.map((o) => (
                <SelectItem key={o} value={o}>{sistemaCos.labels_cliente[o] || o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Prestazione finiture */}
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Livello finiture</Label>
          <Select value={formData.prestazione || ""} onValueChange={(v) => update("prestazione", v)}>
            <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="prestazione-select">
              <SelectValue placeholder="Seleziona" />
            </SelectTrigger>
            <SelectContent>
              {prestFin.options.map((o) => (
                <SelectItem key={o} value={o}>{prestFin.labels_cliente[o] || o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* N. piani + Ascensore */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Numero di piani fuori terra</Label>
          <Input type="number" min={1} max={20} value={formData.n_piani ?? 1}
            onChange={(e) => update("n_piani", e.target.value)}
            className="rounded-xl border-[#E5E2DB] h-11" placeholder="1" data-testid="n-piani-input" />
        </div>
        <label className="flex items-center gap-2 rounded-xl border border-[#E5E2DB] p-3 mt-6 cursor-pointer"
               data-testid="ha-ascensore-toggle">
          <Checkbox checked={!!formData.ha_ascensore}
            onCheckedChange={(v) => update("ha_ascensore", !!v)}
            className="data-[state=checked]:bg-[#163321]" />
          <span className="text-sm">Ascensore presente</span>
        </label>
      </div>

      {/* Legacy fields spersonalizzati (tipologia/qualita) rimossi — motore v1.76.0 */}

      {/* Mq + Piano */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Superficie (mq)</Label>
          <Input type="number" min={15} max={2000} value={formData.mq || ""} onChange={(e) => update("mq", e.target.value)}
            className="rounded-xl border-[#E5E2DB] h-11" placeholder="80" data-testid="mq-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Piano (se appartamento)</Label>
          <Input type="number" value={formData.piano ?? ""} onChange={(e) => update("piano", e.target.value)}
            className="rounded-xl border-[#E5E2DB] h-11" placeholder="3" data-testid="piano-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Anno costruzione</Label>
          <Input type="number" min={1800} max={CURRENT_YEAR} value={formData.anno_costruzione || ""}
            onChange={(e) => update("anno_costruzione", e.target.value)}
            className="rounded-xl border-[#E5E2DB] h-11" placeholder="1990" data-testid="anno-costruzione-input" />
        </div>
      </div>

      {/* Ultima ristrutturazione (opzionale) */}
      <div className="space-y-2 max-w-xs">
        <Label className="text-sm text-[#0F1F14]">Ultima ristrutturazione integrale <span className="text-zinc-400">(opzionale)</span></Label>
        <Input type="number" min={1800} max={CURRENT_YEAR}
          value={formData.anno_ultima_ristrutturazione || ""}
          onChange={(e) => update("anno_ultima_ristrutturazione", e.target.value)}
          className="rounded-xl border-[#E5E2DB] h-11" placeholder="2015" data-testid="anno-ristrutturazione-input" />
      </div>

      {/* Adeguamento ISTAT */}
      <div className="rounded-xl border border-[#E5E2DB] p-4 bg-[#F5F4F0]/50">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="adeguamento-istat-toggle">
          <Checkbox
            checked={!!formData.adeguamento_istat}
            onCheckedChange={(v) => update("adeguamento_istat", !!v)}
            className="mt-0.5 data-[state=checked]:bg-[#163321]"
          />
          <div className="text-sm text-[#0F1F14]">
            <span className="font-medium">Adeguamento automatico dei capitali all'inflazione</span>
            <InfoTooltip title="Adeguamento ISTAT" body={gloss.adeguamento_istat?.body || ""} size={14} testid="tip-istat" />
            {istatPct != null && (
              <p className="text-xs text-[#4A5E51] mt-0.5">
                Adeguamento annuo previsto: <strong>{istatPct}%</strong>
                {istatAnno && ` (ultima inflazione ISTAT ${istatAnno} — indice FOI)`}
              </p>
            )}
          </div>
        </label>
      </div>

      {/* Accessori */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { key: "has_box", label: "Box auto" },
          { key: "has_giardino", label: "Giardino privato" },
          { key: "has_terrazzo", label: "Terrazzo" },
        ].map((a) => (
          <label key={a.key} data-testid={`casa-${a.key.replace("_", "-")}`}
            className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer ${formData[a.key] ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB]"}`}>
            <Checkbox checked={!!formData[a.key]} onCheckedChange={(v) => update(a.key, !!v)} className="data-[state=checked]:bg-[#163321]" />
            <span className="text-sm">{a.label}</span>
          </label>
        ))}
      </div>

      <GeoLocationInput label="Indirizzo immobile" value={formData.indirizzo_immobile}
        onChange={(v) => update("indirizzo_immobile", v)} testId="indirizzo-immobile" placeholder="Via, Città, CAP" />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// STEP Valutazione — chiama /stima-ricostruzione (nuovo motore)
// ═════════════════════════════════════════════════════════════
function _regioneFromAddress(indirizzo) {
  if (!indirizzo || typeof indirizzo !== "object") return null;
  return indirizzo.regione || indirizzo.region || null;
}

function _comuneFromAddress(indirizzo) {
  if (!indirizzo || typeof indirizzo !== "object") return null;
  return indirizzo.comune || indirizzo.city || indirizzo.localita || null;
}

export function CasaStepValutazione({ formData, update }) {
  const [loading, setLoading] = useState(false);
  const [stima, setStima] = useState(formData.valore_ricostruzione_stima || null);
  const [error, setError] = useState("");
  const [override, setOverride] = useState(!!formData.valore_immobile_manuale);
  const [manualValue, setManualValue] = useState(
    formData.valore_immobile_manuale ? String(formData.valore_immobile || "") : ""
  );

  // v1.76.0 — payload nuovo motore canonico
  const payload = useMemo(() => ({
    mq: Number(formData.mq) || 0,
    tipo_immobile: formData.archetipo || null,          // il backend accetta anche `archetipo` diretto
    sistema_costruttivo: formData.sistema_costruttivo || null,
    prestazione: formData.prestazione || null,
    n_piani: Number(formData.n_piani) || 1,
    ha_ascensore: !!formData.ha_ascensore,
    comune: _comuneFromAddress(formData.indirizzo_immobile),
    anno_calcolo: new Date().getFullYear(),
    adeguamento_istat: formData.adeguamento_istat !== false,
  }), [formData]);

  const canCalc = payload.mq >= 15 && !!formData.archetipo && !!payload.sistema_costruttivo && !!payload.prestazione;

  const runStima = useCallback(async () => {
    if (!canCalc) { setError("Completa: tipo di abitazione, sistema costruttivo, livello finiture e mq (>=15)."); return; }
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/public/casa/stima-ricostruzione`, payload, { withCredentials: true });
      setStima(data);
      // Nuovo motore ritorna `totale_ricostruzione` (int). Retro-compat con vecchio v1 `valore_ricostruzione_eur`.
      const totale = data.totale_ricostruzione ?? data.valore_ricostruzione_eur ?? null;
      update({
        valore_ricostruzione_stima: data,
        ...(!override && totale != null ? { valore_immobile: totale, valore_immobile_manuale: false } : {}),
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Errore calcolo stima ricostruzione. Riprova.";
      setError(msg);
    } finally { setLoading(false); }
  }, [canCalc, payload, override, update]);

  useEffect(() => {
    if (canCalc && !stima) { runStima(); }
  }, [canCalc, stima, runStima]);

  const _totale = () => stima?.totale_ricostruzione ?? stima?.valore_ricostruzione_eur ?? null;
  const handleToggleOverride = (v) => {
    setOverride(v);
    if (!v) {
      setManualValue("");
      update({ valore_immobile: _totale(), valore_immobile_manuale: false });
    } else {
      setManualValue(String(_totale() ?? ""));
      update({ valore_immobile: _totale(), valore_immobile_manuale: true });
    }
  };

  const handleManualChange = (e) => {
    const v = e.target.value.replace(/[^\d]/g, "");
    setManualValue(v);
    update({ valore_immobile: v ? Number(v) : null, valore_immobile_manuale: true });
  };

  return (
    <div className="space-y-5" data-testid="casa-step-valutazione">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-lg font-medium text-[#0F1F14] flex items-center gap-2">
            <Home className="w-5 h-5 text-emerald-700" /> Valore di ricostruzione stimato
          </h3>
          <p className="text-sm text-[#4A5E51] mt-1">
            Costo per ricostruire il fabbricato ex novo, escluso il valore del terreno.
          </p>
        </div>
        <button type="button" onClick={runStima} disabled={loading || !canCalc}
          className="rounded-full flex items-center gap-2 border border-[#E5E2DB] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-40"
          data-testid="casa-valutazione-recalc-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Ricalcola
        </button>
      </div>

      {!canCalc && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="casa-valutazione-need-input">
          Torna indietro e completa: tipo di abitazione, sistema costruttivo, livello finiture e superficie (mq ≥ 15).
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="casa-valutazione-error">{error}</div>
      )}

      {stima && !error && (
        <div className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6" data-testid="casa-valutazione-card">
          <p className="text-xs uppercase tracking-wide text-emerald-800 mb-1">Valore di ricostruzione</p>
          <p className="font-heading text-3xl sm:text-4xl font-medium text-[#0F1F14]" data-testid="casa-ricostruzione-value">
            {eurFmt(stima.totale_ricostruzione ?? stima.valore_ricostruzione_eur)}
          </p>
          <div className="mt-3 text-xs text-[#4A5E51] space-y-1" data-testid="casa-ricostruzione-breakdown">
            {stima.breakdown?.fattori ? (
              <>
                <p>€/mq: <strong>{eurFmt(stima.valore_mq_adeguato ?? stima.valore_mq_canonico)}</strong> · {stima.breakdown?.area_mq} mq</p>
                <p>
                  Sistema ×{stima.breakdown.fattori.f_sistema?.toFixed(2)} · Finiture ×{stima.breakdown.fattori.f_prestazione?.toFixed(2)}
                  · Zona sismica {stima.breakdown.zona_sismica} ×{stima.breakdown.fattori.f_sismico?.toFixed(3)}
                  {stima.breakdown.n_piani > 1 && <> · Piani ×{stima.breakdown.fattori.f_piani?.toFixed(3)}</>}
                  {stima.breakdown.ha_ascensore && <> · Ascensore ×{stima.breakdown.fattori.f_ascensore?.toFixed(3)}</>}
                </p>
                {stima.breakdown.inflazione_cumulata_pct > 0 && (
                  <p>Adeguamento ISTAT (base {stima.breakdown.anno_base} → {stima.breakdown.anno_calcolo}): <strong>+{stima.breakdown.inflazione_cumulata_pct}%</strong></p>
                )}
              </>
            ) : (
              <p><strong>Formula:</strong> {stima.breakdown?.formula}</p>
            )}
          </div>
          <p className="text-xs text-[#6B7A72] mt-3 italic">{stima.disclaimer}</p>
        </div>
      )}

      <div className="rounded-xl border border-[#E5E2DB] p-4 space-y-3">
        <label className="flex items-center gap-3 cursor-pointer" data-testid="casa-valutazione-override-toggle">
          <input type="checkbox" checked={override} onChange={(e) => handleToggleOverride(e.target.checked)} className="w-4 h-4 accent-[#163321]" />
          <span className="text-sm text-[#0F1F14]">Ho una perizia più precisa — voglio inserire manualmente il valore</span>
        </label>
        {override && (
          <div className="space-y-2">
            <Label className="text-sm text-[#0F1F14]">Valore ricostruzione manuale (€)</Label>
            <Input type="text" inputMode="numeric" value={manualValue} onChange={handleManualChange}
              className="rounded-xl border-[#E5E2DB] h-11" placeholder="150000" data-testid="casa-valutazione-override-input" />
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// STEP Copertura — accordion 7 partite (v1.76.1) con facoltative,
// massimali/franchigie/livelli/combinazioni dropdown.
// ═════════════════════════════════════════════════════════════

const PARTITA_ICONS = {
  incendio_danni_abitazione: Home,
  furto_rapina: Lock,
  responsabilita_civile: Users,
  cristalli: Square,
  tutela_legale: Scale,
  autosufficienza: HeartPulse,
  assistenza: LifeBuoy,
};

function FranchigiaSelect({ facCodice, fac, formData, update }) {
  const key = `facolt_${facCodice}_franchigia`;
  const val = formData[key] || fac.franchigia_default || "";
  return (
    <div className="mt-2">
      <Label className="text-[11px] uppercase tracking-wide text-[#4A5E51]">Franchigia</Label>
      <Select value={val} onValueChange={(v) => update(key, v)}>
        <SelectTrigger className="rounded-lg border-[#E5E2DB] h-9 text-sm" data-testid={`facolt-${facCodice}-franchigia`}>
          <SelectValue placeholder="Seleziona" />
        </SelectTrigger>
        <SelectContent>
          {(fac.franchigie_disponibili || []).map((f) => (
            <SelectItem key={f.codice} value={f.codice}>{f.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function MassimaleSelect({ facCodice, fac, formData, update }) {
  const key = `facolt_${facCodice}_massimale`;
  const val = formData[key] ?? fac.massimale_default ?? "";
  return (
    <div className="mt-2">
      <Label className="text-[11px] uppercase tracking-wide text-[#4A5E51]">Massimale</Label>
      <Select value={val ? String(val) : ""} onValueChange={(v) => update(key, Number(v))}>
        <SelectTrigger className="rounded-lg border-[#E5E2DB] h-9 text-sm" data-testid={`facolt-${facCodice}-massimale`}>
          <SelectValue placeholder="Seleziona" />
        </SelectTrigger>
        <SelectContent>
          {(fac.massimali_disponibili || []).map((m) => (
            <SelectItem key={m} value={String(m)}>{eurFmt(m)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SubLimiteDropdown({ codice, subCfg, formData, update, dimora }) {
  const key = `sublim_${codice}`;
  const val = formData[key] ?? 0;
  const maxDim = dimora === "saltuaria" ? subCfg.max_saltuaria : subCfg.max_abituale;
  const scaglioni = (subCfg.scaglioni || []).filter((s) => s <= maxDim);
  return (
    <div className="space-y-1">
      <Label className="text-sm text-[#0F1F14]">
        {subCfg.label_cliente || codice}
        {subCfg.note_cliente && (
          <span className="block text-[11px] text-[#6B7A72] font-normal mt-0.5">{subCfg.note_cliente}</span>
        )}
      </Label>
      <Select value={String(val)} onValueChange={(v) => update(key, Number(v))}>
        <SelectTrigger className="rounded-lg border-[#E5E2DB] h-9 text-sm" data-testid={`sublim-${codice}`}>
          <SelectValue placeholder="Seleziona" />
        </SelectTrigger>
        <SelectContent>
          {scaglioni.map((s) => (
            <SelectItem key={s} value={String(s)}>{s === 0 ? "Non assicurato" : eurFmt(s)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PartitaAccordion({ partita, gloss, formData, update }) {
  const [open, setOpen] = useState(!!partita.obbligatoria || !!formData[`partita_${partita.codice}_attiva`]);
  const isAttiva = !!partita.obbligatoria || !!formData[`partita_${partita.codice}_attiva`];

  const toggleAttiva = () => {
    if (partita.obbligatoria) return;
    const next = !isAttiva;
    update(`partita_${partita.codice}_attiva`, next);
    setOpen(next);
  };

  const toggleFacolt = (codice) => {
    const key = `facolt_${codice}`;
    update(key, !formData[key]);
  };

  const isFacoltEnabled = (fac) => {
    if (fac.requires && Array.isArray(fac.requires)) {
      for (const req of fac.requires) {
        if (!formData[`facolt_${req}`]) return false;
      }
    }
    if (fac.requires_flag && !formData[fac.requires_flag]) return false;
    return true;
  };

  const g = gloss[partita.tooltip_ref];
  const Icon = PARTITA_ICONS[partita.codice] || Shield;

  // Partita ha massimale scalare?
  const hasMassimale = Array.isArray(partita.massimali_disponibili);
  // Partita ha combinazioni sinistro/anno?
  const hasCombinazioni = Array.isArray(partita.combinazioni_disponibili);
  // Partita ha livelli?
  const hasLivelli = Array.isArray(partita.livelli_disponibili);
  // Partita Furto ha forma + sub-limiti?
  const isFurto = partita.codice === "furto_rapina";
  const formaFurto = formData.forma_furto || (partita.forma_prestazione?.default || "globale");
  const dimora = formData.dimora || "abituale";

  return (
    <div className="rounded-xl border border-[#E5E2DB] overflow-hidden bg-white" data-testid={`partita-${partita.codice}`}>
      <button type="button"
        onClick={() => (partita.obbligatoria ? setOpen(!open) : toggleAttiva())}
        className="w-full flex items-center justify-between p-4 hover:bg-zinc-50 transition"
        data-testid={`partita-${partita.codice}-header`}>
        <div className="flex items-center gap-3 text-left">
          <Icon className="w-5 h-5 text-emerald-700 shrink-0" />
          <div>
            <div className="font-medium text-[#0F1F14] flex items-center">
              {partita.label_cliente}
              {g && <InfoTooltip title={g.title} body={g.body} ref={g.ref} size={14} testid={`tip-${partita.codice}`} />}
              {partita.obbligatoria && <span className="ml-2 text-[10px] uppercase font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Base</span>}
            </div>
            <p className="text-xs text-[#4A5E51] mt-0.5">{partita.descrizione_cliente}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {!partita.obbligatoria && (
            <span className={`text-xs font-medium px-3 py-1 rounded-full ${isAttiva ? "bg-emerald-600 text-white" : "bg-zinc-200 text-zinc-600"}`}
              data-testid={`partita-${partita.codice}-toggle`}>
              {isAttiva ? "Inclusa" : "Non inclusa"}
            </span>
          )}
          {isAttiva && (open ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />)}
        </div>
      </button>

      {open && isAttiva && (
        <div className="border-t border-[#E5E2DB] p-4 bg-[#F5F4F0]/40 space-y-4" data-testid={`partita-${partita.codice}-body`}>

          {/* Massimale scalare (Cristalli, RC) */}
          {hasMassimale && (
            <div className="space-y-1 max-w-xs">
              <Label className="text-sm text-[#0F1F14]">Massimale</Label>
              <Select value={String(formData[`partita_${partita.codice}_massimale`] ?? partita.massimale_default ?? "")}
                onValueChange={(v) => update(`partita_${partita.codice}_massimale`, Number(v))}>
                <SelectTrigger className="rounded-lg border-[#E5E2DB] h-9 text-sm" data-testid={`partita-${partita.codice}-massimale-select`}>
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent>
                  {partita.massimali_disponibili.map((m) => (
                    <SelectItem key={m} value={String(m)}>{eurFmt(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Combinazioni Tutela Legale (sinistro / anno) */}
          {hasCombinazioni && (
            <div className="space-y-1 max-w-md">
              <Label className="text-sm text-[#0F1F14]">Combinazione massimali</Label>
              <Select value={formData[`partita_${partita.codice}_combinazione`] || partita.combinazione_default || ""}
                onValueChange={(v) => update(`partita_${partita.codice}_combinazione`, v)}>
                <SelectTrigger className="rounded-lg border-[#E5E2DB] h-9 text-sm" data-testid={`partita-${partita.codice}-combinazione-select`}>
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent>
                  {partita.combinazioni_disponibili.map((c) => (
                    <SelectItem key={c.codice} value={c.codice}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Livelli Assistenza (Silver/Gold) */}
          {hasLivelli && (
            <div className="space-y-2">
              <Label className="text-sm text-[#0F1F14]">Livello di assistenza</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {partita.livelli_disponibili.map((l) => {
                  const cur = formData[`partita_${partita.codice}_livello`] || partita.livello_default;
                  const on = cur === l.codice;
                  return (
                    <button key={l.codice} type="button"
                      onClick={() => update(`partita_${partita.codice}_livello`, l.codice)}
                      data-testid={`partita-${partita.codice}-livello-${l.codice}`}
                      className={`text-left rounded-lg border-2 p-3 transition ${on ? "border-emerald-600 bg-emerald-50" : "border-[#E5E2DB] bg-white hover:border-emerald-400"}`}>
                      <div className="text-sm font-medium text-[#0F1F14]">{l.label_cliente}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Furto: forma + sub-limiti */}
          {isFurto && (
            <div className="space-y-3">
              <div className="space-y-1 max-w-md">
                <Label className="text-sm text-[#0F1F14]">Forma di prestazione</Label>
                <RadioGroup value={formaFurto} onValueChange={(v) => update("forma_furto", v)} className="flex flex-wrap gap-3">
                  {(partita.forma_prestazione?.options || []).map((o) => (
                    <label key={o}
                      className={`flex items-center gap-2 rounded-lg border p-2 cursor-pointer flex-1 min-w-[180px] ${formaFurto === o ? "border-emerald-600 bg-emerald-50" : "border-[#E5E2DB]"}`}
                      data-testid={`furto-forma-${o}`}>
                      <RadioGroupItem value={o} id={`forma-${o}`} />
                      <span className="text-sm">{partita.forma_prestazione.labels_cliente?.[o] || o}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {formaFurto === "analitica" && partita.sub_limiti && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-3" data-testid="furto-sublimiti-block">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">Sub-limiti (forma analitica)</p>
                  <p className="text-[11px] text-[#4A5E51] italic">
                    Vincolo: la somma di Preziosi + Denaro + Cassaforte non può superare € 45.000 e comunque non oltre il triplo del Contenuto.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {partita.sub_limiti.preziosi_carte_raccolte && (
                      <SubLimiteDropdown codice="preziosi" subCfg={partita.sub_limiti.preziosi_carte_raccolte} formData={formData} update={update} dimora={dimora} />
                    )}
                    {partita.sub_limiti.denaro && (
                      <SubLimiteDropdown codice="denaro" subCfg={partita.sub_limiti.denaro} formData={formData} update={update} dimora={dimora} />
                    )}
                    {partita.sub_limiti.cassaforte && (
                      <SubLimiteDropdown codice="cassaforte" subCfg={partita.sub_limiti.cassaforte} formData={formData} update={update} dimora={dimora} />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Facoltative (Incendio, Furto, RC) */}
          {(partita.facoltative || []).length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#4A5E51]">Coperture aggiuntive facoltative</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {partita.facoltative.map((fac) => {
                  const enabled = isFacoltEnabled(fac);
                  const active = !!formData[`facolt_${fac.codice}`];
                  const fg = gloss[fac.tooltip_ref];
                  const hasFranchigie = Array.isArray(fac.franchigie_disponibili);
                  const hasMassimali = Array.isArray(fac.massimali_disponibili);
                  return (
                    <div key={fac.codice}
                      data-testid={`facolt-wrap-${fac.codice}`}
                      className={`rounded-xl border-2 p-3 transition ${
                        !enabled ? "opacity-50 border-zinc-200 bg-white"
                        : active ? "border-emerald-600 bg-emerald-50"
                        : "border-[#E5E2DB] bg-white"
                      }`}>
                      <button type="button"
                        disabled={!enabled}
                        onClick={() => toggleFacolt(fac.codice)}
                        data-testid={`facolt-${fac.codice}`}
                        className="w-full text-left">
                        <div className="flex items-start gap-2">
                          <div className={`w-4 h-4 rounded border-2 shrink-0 mt-0.5 ${active ? "bg-emerald-600 border-emerald-600" : "border-zinc-300"}`}>
                            {active && <svg viewBox="0 0 12 12" className="w-full h-full text-white p-0.5"><path fill="currentColor" d="M4.5 8.5L2 6l.7-.7L4.5 7.1 9.3 2.3l.7.7z" /></svg>}
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-[#0F1F14] flex items-center">
                              {fac.label_cliente}
                              {fg && <InfoTooltip title={fg.title} body={fg.body} ref={fg.ref} size={12} testid={`tip-${fac.codice}`} />}
                            </div>
                            <p className="text-xs text-[#4A5E51] mt-0.5">{fac.descrizione_cliente}</p>
                            {!enabled && fac.requires && (
                              <p className="text-[10px] italic text-amber-700 mt-1">Attiva prima: {fac.requires.join(", ")}</p>
                            )}
                            {!enabled && fac.requires_flag && (
                              <p className="text-[10px] italic text-amber-700 mt-1">Disponibile se svolgi attività ricettiva (es. B&amp;B)</p>
                            )}
                          </div>
                        </div>
                      </button>
                      {active && enabled && hasFranchigie && (
                        <FranchigiaSelect facCodice={fac.codice} fac={fac} formData={formData} update={update} />
                      )}
                      {active && enabled && hasMassimali && (
                        <MassimaleSelect facCodice={fac.codice} fac={fac} formData={formData} update={update} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CasaStepCopertura({ formData, update }) {
  const { config, error, retry } = useConfig();
  const partite = config?.partite || [];
  const gloss = config?.glossario || {};
  const softWarnings = config?.soft_warnings_globali || [];

  // Assicura che la partita obbligatoria sia sempre attiva nel form state
  useEffect(() => {
    partite.forEach((p) => {
      if (p.obbligatoria && !formData[`partita_${p.codice}_attiva`]) {
        update(`partita_${p.codice}_attiva`, true);
      }
    });
  }, [partite]); // eslint-disable-line

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800 space-y-3"
           data-testid="casa-config-error-copertura">
        <p><strong>Errore caricamento coperture.</strong> {error}</p>
        <button type="button" onClick={retry}
          className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
          data-testid="casa-config-retry-copertura-btn">Riprova</button>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-400"
           data-testid="casa-config-loading-copertura">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Caricamento coperture…
      </div>
    );
  }

  // v1.76.1 — Soft-warning banner (legno strutturale + rischio estero)
  const activeWarnings = softWarnings.filter((w) => {
    if (w.trigger_field === "sistema_costruttivo" && w.trigger_equals) {
      return formData.sistema_costruttivo === w.trigger_equals;
    }
    if (w.trigger_field === "immobile_paese" && w.trigger_not_equals) {
      const paese = formData.indirizzo_immobile?.paese || formData.indirizzo_immobile?.country || "IT";
      return paese !== w.trigger_not_equals;
    }
    return false;
  });

  return (
    <div className="space-y-4" data-testid="casa-step-copertura">
      {activeWarnings.length > 0 && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="casa-soft-warnings">
          {activeWarnings.map((w) => (
            <div key={w.codice} className="flex items-start gap-2 text-sm text-amber-900" data-testid={`soft-warning-${w.codice}`}>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <p>{w.message_cliente}</p>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Valore contenuto stimato (€)</Label>
        <div className="max-w-xs">
          <Input type="number" value={formData.valore_contenuto || ""}
            onChange={(e) => update("valore_contenuto", e.target.value)}
            className="rounded-xl border-[#E5E2DB] h-11" placeholder="30000" data-testid="valore-contenuto-input" />
        </div>
        <p className="text-xs text-[#4A5E51]">Stima approssimativa: arredamento, elettrodomestici, oggetti personali.</p>
      </div>

      {/* Flag attività B&B (abilita facoltativa dedicata) */}
      <label className="flex items-center gap-3 rounded-xl border border-[#E5E2DB] p-3 cursor-pointer max-w-md"
        data-testid="attivita-bnb-toggle">
        <Checkbox checked={!!formData.attivita_bnb}
          onCheckedChange={(v) => update("attivita_bnb", !!v)}
          className="data-[state=checked]:bg-[#163321]" />
        <span className="text-sm text-[#0F1F14]">Nell'abitazione svolgo attività di B&amp;B / affittacamere</span>
      </label>

      <div className="space-y-3">
        <p className="text-sm text-[#0F1F14] font-medium flex items-center gap-2">
          <Shield className="w-4 h-4 text-emerald-700" /> Coperture della polizza
        </p>
        {partite.map((p) => (
          <PartitaAccordion key={p.codice} partita={p} gloss={gloss} formData={formData} update={update} />
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// Entry points usati da QuestionnairePage
// ═════════════════════════════════════════════════════════════
export function renderCasaStep(step, formData, update /* toggleArray unused */) {
  if (step === 0) return <CasaStep0 formData={formData} update={update} />;
  if (step === 1) return <CasaStepValutazione formData={formData} update={update} />;
  if (step === 2) return <CasaStepCopertura formData={formData} update={update} />;
  return null;
}
